import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import routes from 'constants/routes';
import { AppDispatch, RootState } from 'redux/store';
import { deleteGroupById } from 'redux/slices/announcements/announcementGroupSlice';
import Table, { Column } from 'components/Common/StyledTable';
import DeleteModal from 'components/Common/DeleteModal';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';
import { AnnouncementGroup } from 'types/announcements';

const AnnouncementGroupColumns: Column[] = [
    { id: 'title', label: 'Title' },
    { id: 'totalParticipants', label: 'Participants', align: 'center' },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches AnnouncementContainer's loosely-typed data/loading props
function AnnouncementGroupContainer({ data, loading }: any) {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageAnnouncements = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');
    const { total } = useSelector((state: RootState) => ({
        total: state.announcement.announcementGroups?.total,
    }));
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [group, setGroup] = useState<AnnouncementGroup | null>(null);

    const navigationHandler = (row: AnnouncementGroup) => {
        navigate(routes.announcements.groupDetails.replace(':id', String(row?.id)));
    };

    const handleDeleteClick = useCallback((row: AnnouncementGroup) => {
        setOpenDeleteModal(true);
        setGroup(row);
    }, []);

    const deleteGroup = useCallback(async () => {
        if (!parishId || !group?.id || !canManageAnnouncements) return;
        setDeleteLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dispatched thunk's fulfilled/rejected payload union
        const { payload }: any = await dispatch(deleteGroupById({ parishId, id: group.id }));
        setDeleteLoading(false);
        if (payload?.ok) {
            setOpenDeleteModal(false);
        }
    }, [dispatch, group?.id, parishId, canManageAnnouncements]);

    return (
        <>
            <Table
                unStyled
                navigationHandler={navigationHandler}
                showDeleteCell={canManageAnnouncements}
                onDeleteClick={handleDeleteClick}
                columns={AnnouncementGroupColumns}
                data={data}
                totalCount={total}
                serverSidePagination
                loading={loading}
            />
            <DeleteModal
                isOpen={openDeleteModal}
                title="Delete Group"
                description="Are you sure you want to delete the selected group!"
                onClose={() => setOpenDeleteModal(false)}
                onDelete={deleteGroup}
                loading={deleteLoading}
            />
        </>
    );
}

export default AnnouncementGroupContainer;
